
import React, { useState, useEffect } from 'react';
import { ArrowRight, Clock, TrendingUp, Newspaper } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import CategoryBadge from '@/components/ui/CategoryBadge';
import SuccessStoryGridCard from '@/components/ui/SuccessStoryGridCard';
import { useNavigate } from 'react-router-dom';

// Import success story data
import { successStories } from '@/data/successStories';

const TrendingSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState("trending");
  const navigate = useNavigate();
  
  // Get randomized success stories for display
  const getRandomStories = (allStories, count) => {
    const shuffled = [...allStories].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };
  
  // Initialize with random stories
  const [trendingSuccessStories, setTrendingSuccessStories] = useState(() => 
    getRandomStories(successStories, 3));
  const [latestSuccessStories, setLatestSuccessStories] = useState(() => 
    getRandomStories(successStories.slice(3), 3));
  
  if (trendingSuccessStories.length === 0) return null;
  
  // Select the main featured article (first success story)
  const mainArticle = trendingSuccessStories[0];
  // Get the remaining articles for the side list
  const sideArticles = trendingSuccessStories.slice(1, 3);
  
  // Handler to navigate to the success story detail page
  const handleStoryClick = (storyId: string) => {
    navigate(`/success-story/${storyId}`);
  };
  
  const handleViewAllClick = () => {
    navigate('/success-story');
  };
  
  return (
    <section className="py-14 px-4 md:px-6 bg-gradient-to-b from-purple-50/30 via-white to-secondary/10 border-y border-border/30">
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 border-b pb-4">
          <h2 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            <span className="bg-purple-600 w-5 h-5 rounded-full flex-shrink-0"></span>
            Content Hub
          </h2>
        </div>
        
        <Tabs defaultValue="trending" className="w-full">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-10">
            <div></div> {/* Empty div for spacing */}
            <TabsList className="grid w-full max-w-sm grid-cols-2 bg-purple-100/50">
              <TabsTrigger value="trending" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                <TrendingUp className="w-4 h-4 mr-2" />
                Trending
              </TabsTrigger>
              <TabsTrigger value="latest" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white">
                <Newspaper className="w-4 h-4 mr-2" />
                Latest
              </TabsTrigger>
            </TabsList>
          </div>
          
          <TabsContent value="trending" className="mt-0">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Main featured article - takes 2/3 of the width on large screens */}
              <div className="lg:col-span-2 animate-slide-in" style={{ animationDelay: '0.1s' }}>
                <div className="relative h-[400px] rounded-lg overflow-hidden group shadow-md border border-purple-200/50">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent z-10" />
                  <div className="relative w-full h-full">
                    <img 
                      src={mainArticle.thumbnail || mainArticle.image} 
                      alt={mainArticle.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/placeholder.svg';
                      }}
                    />
                  </div>
                  <div className="absolute top-4 left-4 z-20">
                    <span className="bg-purple-600 text-white text-xs font-bold px-3 py-1 uppercase rounded-md">Featured</span>
                  </div>
                  <div className="absolute top-4 right-4 z-20">
                    <div className="flex items-center gap-1 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75"></span>
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
                      </span>
                      Success Story
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-20">
                    <div className="flex flex-wrap gap-2 mb-2">
                      {mainArticle.stats && mainArticle.stats.revenue && (
                        <span className="bg-amber-500/80 text-white text-xs px-2 py-0.5 rounded-full">
                          {mainArticle.stats.revenue}
                        </span>
                      )}
                      {mainArticle.stats && mainArticle.stats.impact && (
                        <span className="bg-purple-500/80 text-white text-xs px-2 py-0.5 rounded-full">
                          {mainArticle.stats.impact}
                        </span>
                      )}
                    </div>
                    <h3 
                      className="text-2xl md:text-3xl font-bold text-white mb-3 mt-2 cursor-pointer hover:underline"
                      onClick={() => handleStoryClick(mainArticle.id)}
                    >
                      {mainArticle.title}
                    </h3>
                    <div className="text-white/90 mb-4 line-clamp-none">
                      <p>{mainArticle.oneLiner}</p>
                      <p className="mt-2">Meet {mainArticle.name}, a visionary entrepreneur who has transformed challenges into opportunities and created a successful business that inspires many.</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button 
                        className="mt-2 bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={() => handleStoryClick(mainArticle.id)}
                      >
                        Read Full Story <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                      
                      {mainArticle.achieverId && (
                        <Button 
                          className="mt-2 bg-transparent border border-white text-white hover:bg-white/10"
                          onClick={() => navigate(`/achievers?id=${mainArticle.achieverId}`)}
                        >
                          View Achiever
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Side articles list - takes 1/3 of the width on large screens */}
              <div className="lg:col-span-1">
                <div className="space-y-6 divide-y divide-gray-200 bg-white/70 backdrop-blur-sm p-4 rounded-lg border border-purple-100/50 shadow-sm">
                  <h3 className="text-lg font-semibold text-purple-800 pb-2">More Success Stories</h3>
                  {sideArticles.map((article, index) => (
                    <div 
                      key={article.id}
                      className={cn(
                        "group animate-slide-in pt-5 first:pt-0",
                        index > 0 && "pt-6"
                      )}
                      style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                    >
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <h3 
                            className="text-lg font-bold mb-2 group-hover:text-purple-600 transition-colors duration-200 cursor-pointer"
                            onClick={() => handleStoryClick(article.id)}
                          >
                            {article.title}
                          </h3>
                          <div className="text-sm text-muted-foreground">
                            {article.oneLiner}
                            <p className="mt-2 text-xs text-gray-600">
                              {article.name} has created significant impact through determination and innovative approaches.
                            </p>
                          </div>
                          
                          {/* Stats badges */}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {article.stats.revenue && (
                              <span className="bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                {article.stats.revenue}
                              </span>
                            )}
                            {article.stats.impact && (
                              <span className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                {article.stats.impact}
                              </span>
                            )}
                          </div>
                          
                          {/* Achiever link if available */}
                          <div className="mt-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="px-0 py-0 h-auto text-purple-500 hover:text-purple-700 hover:bg-transparent"
                              onClick={() => handleStoryClick(article.id)}
                            >
                              Read Full Story
                            </Button>
                          </div>
                        </div>
                        <div 
                          className="w-24 h-24 overflow-hidden rounded-md flex-shrink-0 shadow-sm cursor-pointer"
                          onClick={() => handleStoryClick(article.id)}
                        >
                          <img 
                            src={article.thumbnail || article.image} 
                            alt={article.title}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="latest" className="mt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestSuccessStories.map((story, index) => (
                <SuccessStoryGridCard 
                  key={story.id}
                  id={story.id}
                  name={story.name}
                  title={story.title}
                  oneLiner={story.oneLiner}
                  image={story.thumbnail || story.image} // Use thumbnail if available, fallback to image
                  stats={story.stats}
                  achieverId={story.achieverId}
                  className={`animate-slide-in h-full`}
                  style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                  onClick={() => handleStoryClick(story.id)}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>
        
        {/* View All Button */}
        <div className="flex justify-center mt-10">
          <Button 
            onClick={handleViewAllClick}
            className="bg-purple-600 hover:bg-purple-700 text-white px-6"
          >
            View All Success Stories
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default TrendingSection;
